import React from 'react';
import type { MockOrder, MockViewerRole } from '../types';

export interface QuickActionsProps {
  order: MockOrder;
  viewer: MockViewerRole;
}

interface ActionDef {
  id: string;
  label: string;
  /** Roles allowed to use this action. Empty = everyone. */
  allowedRoles?: MockViewerRole[];
}

const ACTIONS: ActionDef[] = [
  { id: 'share-waybill', label: 'Share waybill' },
  { id: 'copy-public-link', label: 'Copy public link' },
  {
    id: 'cancel',
    label: 'Cancel order',
    allowedRoles: ['shipper', 'broker'],
  },
  {
    id: 'redispatch',
    label: 'Re-dispatch',
    allowedRoles: ['carrier_org'],
  },
];

const formatLastSeen = (minutes?: number) => {
  if (minutes === undefined) return null;
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} min ago`;
  const h = Math.floor(minutes / 60);
  if (h < 24) return `${h} hr ago`;
  return `${Math.floor(h / 24)} d ago`;
};

/**
 * Bottom strip on the expanded row. Left side carries situational
 * status hints (last seen, route status), right side carries the
 * action buttons gated by viewer role.
 */
export const QuickActions: React.FC<QuickActionsProps> = ({ order, viewer }) => {
  const visibleActions = ACTIONS.filter(
    a => !a.allowedRoles || a.allowedRoles.includes(viewer),
  );
  const lastSeen = formatLastSeen(order.driver?.lastSeenMinutesAgo);

  return (
    <div className="rounded-xl border border-gray-200 bg-gradient-to-b from-white to-gray-50 p-3 shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2 text-[12px] text-gray-600">
          {lastSeen && (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-white px-2.5 py-1 ring-1 ring-gray-200">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
              Driver seen {lastSeen}
            </span>
          )}
          <span className="inline-flex items-center gap-1.5 rounded-full bg-white px-2.5 py-1 ring-1 ring-gray-200">
            Pickup {order.pickup.city} → {order.dropoff.city}
          </span>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-white px-2.5 py-1 ring-1 ring-gray-200">
            Mode: {order.pricingMode.replace('_', ' ')}
          </span>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {visibleActions.map(a => (
            <button
              key={a.id}
              type="button"
              onClick={() => console.log(`[orders-preview] action: ${a.id}`)}
              className="rounded-md border border-gray-200 bg-white px-3 py-1.5 text-[12px] font-medium text-gray-700 hover:border-gray-300 hover:bg-gray-50">
              {a.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};
